// src/pages/DevHub/components/DevPanel.tsx
// Replaces: .dev-panel, .dev-panel-body, .dev-panel-footer, .mock-playground-header

import React from 'react';
import { cn } from '../../../lib/utils';

// ─── Sub-component types ───────────────────────────────────────────────────
interface DevPanelBodyProps {
    children: React.ReactNode;
    className?: string;
}

interface DevPanelFooterProps {
    children: React.ReactNode;
    className?: string;
}

interface DevPanelHeaderProps {
    children: React.ReactNode;
    className?: string;
}

// ─── Sub-components ────────────────────────────────────────────────────────

/*
 * .dev-panel-body:
 *   padding: 16px;
 *   flex: 1;
 */
const DevPanelBody: React.FC<DevPanelBodyProps> = ({ children, className }) => (
    <div className={cn('p-[16px] flex-1', className)}>
        {children}
    </div>
);

/*
 * .dev-panel-footer:
 *   padding: 12px 20px;
 *   border-top: 1px solid #dadce0;
 *   background: #f8f9fa;
 */
const DevPanelFooter: React.FC<DevPanelFooterProps> = ({ children, className }) => (
    <div className={cn('px-[20px] py-[12px] border-t border-[#dadce0] bg-[#f8f9fa]', className)}>
        {children}
    </div>
);

/*
 * .mock-playground-header:
 *   padding: 10px 20px;
 *   border-bottom: 1px solid #dadce0;
 *   display: flex;
 *   justify-content: space-between;
 *   align-items: center;
 *   background: #f8f9fa;
 *
 * .mock-playground-header h3:
 *   font-size: 1.125rem;
 *   font-weight: 500;
 *   margin: 0 !important;
 *   color: #202124;
 *   border-bottom: none !important;
 */
const DevPanelHeader: React.FC<DevPanelHeaderProps> = ({ children, className }) => (
    <div className={cn(
        'px-[20px] py-[10px]',
        'border-b border-[#dadce0]',
        'flex justify-between items-center',
        'bg-[#f8f9fa]',
        // Ensure any h3 inside follows mock-playground-header h3 rules
        '[&_h3]:text-[1.125rem] [&_h3]:font-medium [&_h3]:m-0 [&_h3]:text-[#202124] [&_h3]:border-b-0',
        className,
    )}>
        {children}
    </div>
);

// ─── Main component type ───────────────────────────────────────────────────
interface DevPanelProps {
    children: React.ReactNode;
    className?: string;
}

type DevPanelComponent = React.FC<DevPanelProps> & {
    Body: React.FC<DevPanelBodyProps>;
    Footer: React.FC<DevPanelFooterProps>;
    Header: React.FC<DevPanelHeaderProps>;
};

/*
 * .dev-panel:
 *   background: #ffffff;
 *   border: 1px solid #dadce0;
 *   border-radius: 12px;
 *   box-shadow: 0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15);
 *   overflow: hidden;
 *   display: flex;
 *   flex-direction: column;
 */
const DevPanel: DevPanelComponent = ({ children, className }) => (
    <div className={cn(
        'bg-white border border-[#dadce0] rounded-[12px]',
        'shadow-[0_1px_2px_0_rgba(60,64,67,0.3),0_1px_3px_1px_rgba(60,64,67,0.15)]',
        'overflow-hidden flex flex-col',
        className,
    )}>
        {children}
    </div>
);

DevPanel.Body = DevPanelBody;
DevPanel.Footer = DevPanelFooter;
DevPanel.Header = DevPanelHeader;

export default DevPanel;
